import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthSafe } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';
import { getInjectedPartnerConfig, type PartnerConfig } from '@/lib/domainConfig';

interface PartnerBrandingData {
  company_name: string;
  logo_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  font_display: string | null;
  font_body: string | null;
  tagline: string | null;
  support_email: string | null;
  support_phone: string | null;
  privacy_url: string | null;
  terms_url: string | null;
  show_powered_by: boolean | null;
}

const DEFAULT_BRANDING: PartnerBrandingData = {
  company_name: 'SettleWise',
  logo_url: null,
  logo_dark_url: null,
  favicon_url: null,
  primary_color: null,
  secondary_color: null,
  accent_color: null,
  font_display: null,
  font_body: null,
  tagline: null,
  support_email: null,
  support_phone: null,
  privacy_url: null,
  terms_url: null,
  show_powered_by: true,
};

interface BrandingContextType {
  branding: PartnerBrandingData;
  isWhiteLabeled: boolean;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextType>({
  branding: DEFAULT_BRANDING,
  isWhiteLabeled: false,
  isLoading: false,
});

export function useBranding() {
  return useContext(BrandingContext);
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuthSafe();
  const isAdvisor = profile?.is_advisor === true;

  // Check for config injected by Cloudflare Worker (partner domain access)
  const injectedConfig = useMemo(() => getInjectedPartnerConfig(), []);

  // Fetch branding for clients (via their advisor connection)
  const { data: clientBranding, isLoading: isClientLoading } = useQuery({
    queryKey: ['client-branding', user?.id],
    queryFn: async () => {
      const { data: connection, error: connError } = await supabase
        .from('advisor_connections')
        .select('advisor_user_id')
        .eq('client_user_id', user!.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (connError) {
        trackSupabaseError('select', 'advisor_connections', connError);
        return null;
      }
      if (!connection?.advisor_user_id) return null;

      const { data: advisorProfile, error: apError } = await supabase
        .from('advisor_profiles')
        .select('id')
        .eq('user_id', connection.advisor_user_id)
        .maybeSingle();

      if (apError) {
        trackSupabaseError('select', 'advisor_profiles', apError);
        return null;
      }
      if (!advisorProfile?.id) return null;

      const { data: brandingData, error: brandError } = await supabase
        .from('partner_branding')
        .select('*')
        .eq('partner_id', advisorProfile.id)
        .maybeSingle();

      if (brandError) {
        trackSupabaseError('select', 'partner_branding', brandError);
        return null;
      }

      return brandingData as PartnerBrandingData | null;
    },
    enabled: !!user?.id && !isAdvisor && !injectedConfig,
    staleTime: 1000 * 60 * 10,
  });

  // Fetch branding for advisors (their own partner_branding record)
  const { data: advisorBranding, isLoading: isAdvisorLoading } = useQuery({
    queryKey: ['advisor-own-branding', user?.id],
    queryFn: async () => {
      const { data: advisorProfile, error: apError } = await supabase
        .from('advisor_profiles')
        .select('id')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (apError) {
        trackSupabaseError('select', 'advisor_profiles', apError);
        return null;
      }
      if (!advisorProfile?.id) return null;

      const { data: brandingData, error: brandError } = await supabase
        .from('partner_branding')
        .select('*')
        .eq('partner_id', advisorProfile.id)
        .maybeSingle();

      if (brandError) {
        trackSupabaseError('select', 'partner_branding', brandError);
        return null;
      }

      return brandingData as PartnerBrandingData | null;
    },
    enabled: !!user?.id && isAdvisor && !injectedConfig,
    staleTime: 1000 * 60 * 10,
  });

  const branding = clientBranding || advisorBranding;
  const isLoading = isClientLoading || isAdvisorLoading;

  // Map injected config to PartnerBrandingData format if available
  const injectedBranding: PartnerBrandingData | null = injectedConfig ? {
    company_name: injectedConfig.companyName,
    logo_url: injectedConfig.logoUrl,
    logo_dark_url: injectedConfig.logoDarkUrl,
    favicon_url: injectedConfig.faviconUrl,
    primary_color: injectedConfig.primaryColor,
    secondary_color: injectedConfig.secondaryColor,
    accent_color: injectedConfig.accentColor,
    font_display: injectedConfig.fontDisplay,
    font_body: injectedConfig.fontBody,
    tagline: injectedConfig.tagline,
    support_email: injectedConfig.supportEmail,
    support_phone: injectedConfig.supportPhone,
    privacy_url: injectedConfig.privacyUrl,
    terms_url: injectedConfig.termsUrl,
    show_powered_by: injectedConfig.showPoweredBy,
  } : null;

  const resolvedBranding = injectedBranding || branding;
  const isWhiteLabeled = !!resolvedBranding;
  const activeBranding = resolvedBranding || DEFAULT_BRANDING;

  // Inject CSS custom properties for partner colors
  useEffect(() => {
    if (!resolvedBranding) return;

    const root = document.documentElement.style;
    if (resolvedBranding.primary_color) root.setProperty('--partner-primary', resolvedBranding.primary_color);
    if (resolvedBranding.secondary_color) root.setProperty('--partner-secondary', resolvedBranding.secondary_color);
    if (resolvedBranding.accent_color) root.setProperty('--partner-accent', resolvedBranding.accent_color);

    // Update favicon if partner has one
    if (resolvedBranding.favicon_url) {
      const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (link) link.href = resolvedBranding.favicon_url;
    }

    return () => {
      root.removeProperty('--partner-primary');
      root.removeProperty('--partner-secondary');
      root.removeProperty('--partner-accent');
    };
  }, [resolvedBranding]);

  const value = useMemo(() => ({
    branding: activeBranding,
    isWhiteLabeled,
    isLoading,
  }), [activeBranding, isWhiteLabeled, isLoading]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}
